import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getFileName } from "../../../helpers/functions";
import useSettings from "../../../hooks/useSettings";
import { Hero } from "./Webinar";
import { PageLoader } from "../NewLandingPage/components/PageLoader";


const getAwardTitle = (url) => {
  const fileName = decodeURIComponent(getFileName(url)).toLowerCase();

  // Numeric date formats
  const numericDateRegex =
    /(\d{4}[-_/]\d{2}[-_/]\d{2}|\d{2}[-_/]\d{2}[-_/]\d{4})/;

  // Textual date formats: 11th june 2024, 3rd July 2023
  const textDateRegex =
    /(\d{1,2}(st|nd|rd|th)?\s+(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{4})/i;

  const numericMatch = fileName.match(numericDateRegex);
  const textMatch = fileName.match(textDateRegex);

  if (numericMatch) {
    return `View Award - ${numericMatch[0]}`;
  }

  if (textMatch) {
    // Capitalize first letter nicely
    return `View Award - ${textMatch[0]
      .split(" ")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")}`;
  }

  return "Reducted Award";
};


const Awards = () => {
  const { setting } = useSettings();
  const [criticalImagesLoaded, setCriticalImagesLoaded] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleOpenPdf = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const criticalImages = [
      "https://justact-api-uploads.s3.ap-south-1.amazonaws.com/13/1766137118039-61ccb099-fb3b-4051-b6f2-4b1a2fc76f5a/awards.png",
    ].filter(Boolean);

    if (criticalImages.length === 0) {
      setCriticalImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const totalImages = criticalImages.length;

    criticalImages.forEach((src) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setTimeout(() => setCriticalImagesLoaded(true), 300);
        }
      };
      img.src = src;
    });

    const timeout = setTimeout(() => setCriticalImagesLoaded(true), 3000);
    return () => clearTimeout(timeout);
  }, []);

  const awards = setting?.reductedAwardsUrl?.split(",") || [];

  return (
    <ResponsiveWrapper>
      <PageLoader isLoading={!criticalImagesLoaded} />
      <Container>
        <Hero
          style={{
            background: `linear-gradient(to bottom, #3B2D23 0%, #1C130F 180%)`,
          }}
          // bgImage={
          //   "https://justact-api-uploads.s3.ap-south-1.amazonaws.com/13/1766137118039-61ccb099-fb3b-4051-b6f2-4b1a2fc76f5a/awards.png"
          // }
        >
          <HeroContent>
            <HeroTitle>Awards</HeroTitle>
          </HeroContent>
        </Hero>
        <AwardsSection>
          {awards.length > 0 ? (
            <GridContainer>
              {awards.map((award, index) => (
                <AwardCard
                  key={index}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  isHovered={hoveredCard === index}
                >
                  <ImageWrapper>
                    <AwardImage
                      src={
                        "https://justact-api-uploads.s3.ap-south-1.amazonaws.com/13/1766137118039-61ccb099-fb3b-4051-b6f2-4b1a2fc76f5a/awards.png"
                      }
                      alt={`${decodeURIComponent(
                        getFileName(award)
                      )} thumbnail`}
                      loading="lazy"
                    />
                    <ImageOverlay isHovered={hoveredCard === index}>
                      <ViewButton onClick={() => handleOpenPdf(award)}>
                        <span>View Award</span>
                      </ViewButton>
                    </ImageOverlay>
                  </ImageWrapper>
                  <CardContent>
                    <AwardTitle>{getAwardTitle(award)}</AwardTitle>
                  </CardContent>
                </AwardCard>
              ))}
            </GridContainer>
          ) : (
            <EmptyState>
              <EmptyTitle>No Awards Yet</EmptyTitle>
            </EmptyState>
          )}
        </AwardsSection>
      </Container>
    </ResponsiveWrapper>
  );
};

export default Awards;

// Styled Components
export const Container = styled.div`
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  width: 100%;
  overflow-x: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AwardsSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const AwardCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 2px solid transparent;
  position: relative;

  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 16px 48px rgba(0, 95, 102, 0.2);
  }

  @media (max-width: 480px) {
    &:hover {
      transform: translateY(-8px);
    }
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 280px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  @media (max-width: 768px) {
    height: 240px;
  }

  @media (max-width: 480px) {
    height: 220px;
  }
`;

const AwardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  filter: saturate(70%) brightness(90%);

  ${AwardCard}:hover & {
    transform: scale(1.1);
    filter: saturate(100%) brightness(100%);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.isHovered ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const ViewButton = styled.div`
  background: white;
  color: #00838c;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transform: translateY(10px);
  transition: transform 0.3s ease;

  ${ImageOverlay}:hover & {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const CardContent = styled.div`
  padding: 1.8rem;
  background: #ffffff;

  @media (max-width: 480px) {
    padding: 1.3rem;
  }
`;

const AwardTitle = styled.h3`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1.2rem;
  line-height: 1.5;
  color: #293461;
  margin: 0 0 0.8rem 0;
  transition: color 0.3s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.8rem;
  color: #293461;
  margin-bottom: 0.5rem;
  font-family: openSans-SemiBold, sans-serif;
`;

const ResponsiveWrapper = styled.div`
  width: 100%;
  min-width: 320px;
  overflow-x: hidden;
`;
